import type { MutableRefObject } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { shouldDragMultiFromPriorSelection } from '@/lib/canvas/canvas-pointer-move'
import { collectGroupTransformInitials } from '@/lib/groups/shape-group-tree'
import { isGroupShape } from '@/lib/groups/group-kind'
import { unionCanvasAabbForShapes } from '../helpers/multi-select-geometry'
import type { InteractionState } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'

/**
 * Gesture-start positions for a move that drags several shapes at once, or `undefined` for a
 * single-shape move (and for any other gesture type).
 */
export function resolveMultiMoveInitials({
  type,
  fromShapeBody,
  effectiveClickedShapeInMultiSelect,
  effectiveSelectedShapes,
  selectedIds,
  shapes,
}: {
  type: InteractionState['type']
  fromShapeBody: boolean
  effectiveClickedShapeInMultiSelect: string | undefined
  effectiveSelectedShapes: ShapeData[]
  selectedIds: string[]
  shapes: ShapeData[]
}): InteractionState['multiMoveInitials'] {
  let multiMoveInitials: InteractionState['multiMoveInitials']

  if (type === 'move') {
    if (fromShapeBody && effectiveClickedShapeInMultiSelect) {
      const ids = selectedIds

      multiMoveInitials = shouldDragMultiFromPriorSelection(ids, effectiveClickedShapeInMultiSelect)
        ? Object.fromEntries(
            ids
              .map((id) => {
                const s = shapes.find((sh) => sh.id === id)

                return s ? ([id, { x: s.x, y: s.y }] as const) : null
              })
              .filter((e): e is [string, { x: number; y: number }] => e !== null)
          )
        : undefined
    } else if (effectiveSelectedShapes.length > 1) {
      multiMoveInitials = Object.fromEntries(
        effectiveSelectedShapes.map((s) => [s.id, { x: s.x, y: s.y }])
      )
    } else {
      multiMoveInitials = undefined
    }
  } else {
    multiMoveInitials = undefined
  }

  return multiMoveInitials
}

/**
 * Gesture-start state for a multi-select rotate: per-shape initials, the pivot, and the chrome's
 * starting angle/size. Also records the chrome "before" snapshot for history.
 */
export function resolveMultiRotateStart({
  type,
  selectedShapes,
  chromeAtStart,
  pendingRotateChromeBeforeRef,
}: {
  type: InteractionState['type']
  selectedShapes: ShapeData[]
  chromeAtStart: MultiSelectChrome | null
  pendingRotateChromeBeforeRef: MutableRefObject<MultiSelectChrome | null>
}): Pick<
  InteractionState,
  | 'multiRotateInitials'
  | 'rotatePivotLocal'
  | 'multiChromeRotationStartDeg'
  | 'multiChromeSizeStart'
> {
  let multiRotateInitials: InteractionState['multiRotateInitials']
  let rotatePivotLocal: InteractionState['rotatePivotLocal']
  let multiChromeRotationStartDeg: InteractionState['multiChromeRotationStartDeg'] = undefined
  let multiChromeSizeStart: InteractionState['multiChromeSizeStart']

  if (type === 'rotate' && selectedShapes.length > 1) {
    multiRotateInitials = Object.fromEntries(
      selectedShapes.map((s) => [
        s.id,
        {
          x: s.x,
          y: s.y,
          rotation: s.rotation ?? 0,
          width: s.width,
          height: s.height,
        },
      ])
    )
    multiChromeRotationStartDeg = chromeAtStart?.rotationDeg ?? 0

    if (chromeAtStart) {
      rotatePivotLocal = { x: chromeAtStart.pivotX, y: chromeAtStart.pivotY }
      multiChromeSizeStart = { width: chromeAtStart.width, height: chromeAtStart.height }
      pendingRotateChromeBeforeRef.current = { ...chromeAtStart }
    } else {
      const u = unionCanvasAabbForShapes(selectedShapes)

      rotatePivotLocal = u
        ? { x: u.minX + (u.maxX - u.minX) / 2, y: u.minY + (u.maxY - u.minY) / 2 }
        : { x: 0, y: 0 }

      if (u) {
        const startChrome: MultiSelectChrome = {
          pivotX: u.minX + (u.maxX - u.minX) / 2,
          pivotY: u.minY + (u.maxY - u.minY) / 2,
          width: u.maxX - u.minX,
          height: u.maxY - u.minY,
          rotationDeg: 0,
        }

        multiChromeSizeStart = { width: startChrome.width, height: startChrome.height }
        pendingRotateChromeBeforeRef.current = { ...startChrome }
      }
    }
  }

  return {
    multiRotateInitials,
    rotatePivotLocal,
    multiChromeRotationStartDeg,
    multiChromeSizeStart,
  }
}

/** Gesture-start snapshots for a single selected group: its descendants' geometry and photo fills. */
export function resolveGroupTransformInitials({
  effectiveSelectedShapes,
  primary,
  shapes,
}: {
  effectiveSelectedShapes: ShapeData[]
  primary: ShapeData
  shapes: ShapeData[]
}): Pick<InteractionState, 'groupTransformInitials' | 'groupPhotoFillInitials'> {
  const groupTransformInitials =
    effectiveSelectedShapes.length === 1 && isGroupShape(primary)
      ? collectGroupTransformInitials(shapes, primary.id)
      : undefined

  const groupPhotoFillInitials = groupTransformInitials
    ? Object.fromEntries(
        Object.keys(groupTransformInitials)
          .map((id): [string, ShapePhotoFill] | null => {
            const photoFill = shapes.find((s) => s.id === id)?.photoFill

            return photoFill ? [id, photoFill] : null
          })
          .filter((entry): entry is [string, ShapePhotoFill] => entry != null)
      )
    : undefined

  return { groupTransformInitials, groupPhotoFillInitials }
}
